import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileModel } from '../../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { CoordinateManager } from '../../../../../../viewer/coordinate-manager';
import { Poi } from '../../../../../../viewer/elements/poi/poi';
import { useNonce } from '../../../../../hooks/use-nonce';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { Icon } from '../../../../icon/icon';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Stack } from '../../../../stack/stack';
import { AssetTags } from '../../components/asset-tags';
import { AssetTimeStamp } from '../../components/asset-timestamp';
import { TimelineSection } from '../../components/timeline-section';
import { PoiFilesList } from '../../dialogs/poi-dialog/poi-files-list';
import { FullWidth } from '../../styles/grid';
import { stringOrDefault } from '../../utils/formatting';

type Props = {
  asset: Poi;
  refreshNonce: number;
  refreshAsset: () => void;
};

enum FilesStatus {
  Idle,
  Loading,
  Error,
}

const PoiBody = (props: Props) => {
  const { t } = useTranslation();
  const [refreshFilesNonce, refreshFiles] = useNonce();

  const [filesStatus, setFilesStatus] = React.useState(FilesStatus.Idle);
  const [files, setFiles] = React.useState<FileModel[]>([]);
  const { isSharedView, sharedViewId } = useSharedView();

  React.useEffect(() => {
    const fetchFiles = async () => {
      try {
        setFilesStatus(FilesStatus.Loading);

        const { data: files } = await SkyMapAxiosServiceFactory.instance
          .createPoiServiceV1()
          .getPoiAssociateFiles({
            path: {
              poiId: props.asset.assetId!,
            },
          });

        setFiles(files);
        setFilesStatus(FilesStatus.Idle);
      } catch (err) {
        setFilesStatus(FilesStatus.Error);
      }
    };

    void fetchFiles();
  }, [props.asset.assetId, refreshFilesNonce, props.refreshNonce, sharedViewId]);

  const renderFiles = () => {
    switch (filesStatus) {
      case FilesStatus.Loading:
        return (
          <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'spinner'], spin: true }}>
            {t('oneMoment', { ns: 'common' })}
          </InfoBox>
        );

      case FilesStatus.Error:
        return (
          <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
            <Stack direction="row" justifyContent={'space-between'} spacing={0.5}>
              <span>{t('poi.errorReadingFiles', { ns: 'skyviewAssetMenu' })}</span>
              <Icon
                icon={['fad', 'arrows-rotate']}
                onClick={() => {
                  refreshFiles();
                }}
              />
            </Stack>
          </InfoBox>
        );

      case FilesStatus.Idle:
        return files.length === 0 ? (
          <InfoBox color="yellow">{t('poi.noFilesExist', { ns: 'skyviewAssetMenu' })}</InfoBox>
        ) : (
          <PoiFilesList files={files} showRemoveIcon={false} />
        );
    }
  };

  const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo();
  const xCoord: number = props.asset.position.x + (projectionOrigo?.x ?? 0);
  const yCoord: number = props.asset.position.y + (projectionOrigo?.y ?? 0);

  return (
    <>
      <FullWidth>
        <LabelledContainer text={t('poi.section.text', { ns: 'skyviewAssetMenu' })}>
          {stringOrDefault(props.asset.properties.comment)}
        </LabelledContainer>
      </FullWidth>

      <LabelledContainer text={t('poi.section.northing', { ns: 'skyviewAssetMenu' })}>
        {yCoord.toFixed(2)}
      </LabelledContainer>

      <LabelledContainer text={t('poi.section.easting', { ns: 'skyviewAssetMenu' })}>
        {xCoord.toFixed(2)}
      </LabelledContainer>

      <LabelledContainer text={t('poi.section.mas', { ns: 'skyviewAssetMenu' })}>
        {props.asset.properties.metersAboveMeanSeaLevel.toFixed(2)}
      </LabelledContainer>

      <FullWidth apply={false} />

      <TimelineSection asset={props.asset} />

      <FullWidth apply={false} />

      {!isSharedView && props.asset.assetId && (
        <FullWidth>
          <LabelledContainer text={t('fileManager.actionBar.tags', { ns: 'components' })}>
            <AssetTags
              initialTagsIds={props.asset.assetTagIds}
              refreshAsset={props.refreshAsset}
              target={{ kind: 'poi', id: props.asset.assetId, name: props.asset.name }}
            />
          </LabelledContainer>
        </FullWidth>
      )}

      <FullWidth>
        <LabelledContainer text={t('poi.section.files', { ns: 'skyviewAssetMenu' })}>
          {renderFiles()}
        </LabelledContainer>
      </FullWidth>

      <FullWidth>
        <AssetTimeStamp
          meta={{
            updatedAt: props.asset.properties.updatedAt,
            updatedBy: props.asset.properties.updatedBy,
            createdAt: props.asset.properties.createdAt,
            createdBy: props.asset.properties.createdBy,
          }}
        />
      </FullWidth>
    </>
  );
};

export { PoiBody, Props as PoiBodyProps };
